import { useTheme } from '@/hooks/useTheme';
import { useEffect, useState, memo } from 'react';

/**
 * Theme-specific visual effects overlay
 * Each theme has unique atmospheric effects
 * Respects reduced motion preferences
 */
const ThemeEffects = memo(() => {
  const { theme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [flashVisible, setFlashVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Check for force-reduced-motion class or media query
    const checkMotion = () => {
      const forced = document.documentElement.classList.contains('force-reduced-motion');
      const media = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      setReducedMotion(forced || media);
    };
    checkMotion();
    
    // Observe class changes on html element
    const observer = new MutationObserver(checkMotion);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    mq.addEventListener('change', checkMotion);
    
    return () => { observer.disconnect(); mq.removeEventListener('change', checkMotion); };
  }, []);

  // Eclipse theme flash effect
  useEffect(() => {
    if (theme !== 'eclipse' || reducedMotion) return;
    
    const flashInterval = setInterval(() => {
      if (Math.random() < 0.1) {
        setFlashVisible(true);
        setTimeout(() => setFlashVisible(false), 100);
      }
    }, 3000);

    return () => clearInterval(flashInterval);
  }, [theme, reducedMotion]);

  if (!mounted || reducedMotion) return null;

  return (
    <>
      {/* ASH theme - Ember particles floating upward */}
      {theme === 'ash' && (
        <div className="fixed inset-0 pointer-events-none z-[100] overflow-hidden">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-destructive/30 animate-ember"
              style={{
                left: `${Math.random() * 100}%`,
                bottom: `-5%`,
                animationDelay: `${Math.random() * 8}s`,
                animationDuration: `${6 + Math.random() * 4}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* TWILIGHT theme - Subtle stars twinkling */}
      {theme === 'twilight' && (
        <div className="fixed inset-0 pointer-events-none z-[100] overflow-hidden">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute w-0.5 h-0.5 rounded-full bg-primary/40 animate-twinkle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 60}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${2 + Math.random() * 3}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* VOID theme - Edge darkness vignette with pulse */}
      {theme === 'void' && (
        <>
          <div className="fixed inset-0 pointer-events-none z-[100] animate-void-pulse">
            <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black/80" />
          </div>
          <div className="fixed inset-0 pointer-events-none z-[101] overflow-hidden">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="absolute w-full h-px bg-white/5 animate-void-glitch"
                style={{
                  top: `${20 + i * 30}%`,
                  animationDelay: `${i * 2}s`,
                }}
              />
            ))}
          </div>
        </>
      )}

      {/* ECLIPSE theme - Pure black with occasional light flashes */}
      {theme === 'eclipse' && (
        <>
          <div className="fixed inset-0 pointer-events-none z-[100]">
            <div className="absolute inset-0 bg-gradient-radial from-transparent via-black/50 to-black/95" />
          </div>
          {flashVisible && (
            <div className="fixed inset-0 pointer-events-none z-[102] bg-white/10 animate-eclipse-flash" />
          )}
          <div className="fixed inset-0 pointer-events-none z-[101] flex items-center justify-center">
            <div className="w-32 h-32 rounded-full animate-eclipse-corona opacity-20" 
                 style={{ 
                   background: 'radial-gradient(circle, transparent 30%, hsl(40, 100%, 50%) 60%, transparent 80%)',
                   filter: 'blur(20px)'
                 }} 
            />
          </div>
        </>
      )}

      {/* DAWN theme - Warm orange/pink gradients */}
      {theme === 'dawn' && (
        <>
          <div className="fixed inset-0 pointer-events-none z-[100]">
            <div className="absolute inset-0 animate-dawn-gradient" 
                 style={{
                   background: 'linear-gradient(180deg, transparent 0%, hsla(20, 80%, 60%, 0.05) 50%, hsla(340, 60%, 50%, 0.08) 100%)'
                 }}
            />
          </div>
          <div className="fixed inset-0 pointer-events-none z-[101] overflow-hidden">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1.5 h-1.5 rounded-full animate-dawn-particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${60 + Math.random() * 40}%`,
                  background: `hsla(${30 + Math.random() * 20}, 80%, 70%, 0.4)`,
                  animationDelay: `${Math.random() * 6}s`,
                  animationDuration: `${8 + Math.random() * 4}s`,
                }}
              />
            ))}
          </div>
        </>
      )}

      {/* DARK theme - Subtle grain texture */}
      {theme === 'dark' && (
        <div className="fixed inset-0 pointer-events-none z-[100] opacity-[0.015]">
          <div className="absolute inset-0 bg-noise animate-grain" />
        </div>
      )}
    </>
  );
});

ThemeEffects.displayName = 'ThemeEffects';

export default ThemeEffects;
